import { Link } from 'react-router'
import { useNow } from '@/hooks/useNow'
import { relativeTime } from '@/lib/format'
import type { AppNotification } from '@/lib/types'

/*
 * Board rows, not cards. Unread is marked with a signal rule down the leading
 * edge — the same amber that means "in transit" on the board itself — so the
 * unread set can be counted from the shape of the list without reading a word.
 */
function NotificationBody({ item }: { item: AppNotification }) {
  const now = useNow(60_000)
  const isUnread = item.readAt === null

  return (
    <div
      className={`border-l-2 py-3 pl-3 ${isUnread ? 'border-accent' : 'border-transparent'}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className={`signage-tight min-w-0 text-sm ${isUnread ? 'text-ink' : 'text-muted'}`}>
          {item.title}
          {isUnread ? <span className="sr-only"> (unread)</span> : null}
        </p>
        <span className="shrink-0 font-mono text-[11px] whitespace-nowrap text-faint">
          {relativeTime(item.createdAt, now)}
        </span>
      </div>
      {item.body ? <p className="mt-1 text-sm text-muted">{item.body}</p> : null}
    </div>
  )
}

export function NotificationList({ items }: { items: AppNotification[] }) {
  return (
    <ul>
      {items.map((item) => (
        <li key={item.id} className="board-row">
          {item.url ? (
            item.url.startsWith('/') ? (
              <Link to={item.url} className="-mx-2 block px-2 transition-colors hover:bg-elevated">
                <NotificationBody item={item} />
              </Link>
            ) : (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer noopener"
                className="-mx-2 block px-2 transition-colors hover:bg-elevated"
              >
                <NotificationBody item={item} />
              </a>
            )
          ) : (
            <NotificationBody item={item} />
          )}
        </li>
      ))}
    </ul>
  )
}
